import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, MessageSquare, Mail } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { useLang } from "@/LanguageContext";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", job: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const digits = form.phone.replace(/\D/g, "").length;
  const valid = form.name.trim() && digits >= 10 && form.job.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error(t.quote.errValidation);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        job: form.job.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(`${t.quote.errSend} ${PHONE_DISPLAY}`);
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full rounded-sm border border-steel-700 bg-steel-950 px-4 py-3.5 text-base text-steel-50 placeholder:text-steel-400/70 transition-colors duration-200 focus:border-forge focus:outline-none focus:ring-2 focus:ring-forge/60";

  return (
    <section id="quote" data-testid="quote-section" className="border-b border-steel-700 bg-steel-900 py-20 md:py-32">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-4 md:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
        <div>
          <SectionHead
            label={t.quote.label}
            title={t.quote.title}
            sub={t.quote.sub}
            testid="quote-head"
          />
          <div className="flex flex-wrap gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="quote-call-btn"
              className="flex items-center gap-2 rounded-sm bg-forge px-6 py-3.5 font-head text-base font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-forge-light"
            >
              <Phone size={17} strokeWidth={2.4} /> {PHONE_DISPLAY}
            </a>
            <a
              href={`sms:${PHONE_TEL}`}
              data-testid="quote-text-btn"
              className="flex items-center gap-2 rounded-sm border border-steel-700 px-6 py-3.5 font-head text-base font-bold uppercase tracking-wide text-steel-50 transition-colors duration-200 hover:border-forge hover:text-forge-light"
            >
              <MessageSquare size={17} strokeWidth={2.4} /> {t.quote.textBtn}
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="quote-email-btn"
              className="flex items-center gap-2 rounded-sm border border-steel-700 px-6 py-3.5 font-head text-base font-bold uppercase tracking-wide text-steel-50 transition-colors duration-200 hover:border-forge hover:text-forge-light"
            >
              <Mail size={17} strokeWidth={2.4} /> {t.quote.emailBtn}
            </a>
          </div>
        </div>

        <div className="rounded-sm border border-steel-700 bg-steel-950 p-6 md:p-10">
          {done ? (
            <div data-testid="quote-confirmation" className="flex min-h-[340px] flex-col justify-center">
              <CheckCircle2 size={44} className="text-forge" />
              <h3 className="mt-6 font-head text-3xl font-black uppercase leading-tight tracking-tight text-steel-50 sm:text-4xl">
                {t.quote.doneTitle}
              </h3>
              <p className="mt-4 text-base text-steel-400">
                {t.quote.doneBody} {PHONE_DISPLAY} {t.quote.doneBodyEnd}
              </p>
              <button
                type="button"
                data-testid="quote-reset-btn"
                onClick={() => {
                  setDone(false);
                  setForm({ name: "", phone: "", job: "" });
                }}
                className="mt-8 w-fit rounded-sm border border-steel-700 px-5 py-3 font-head text-sm font-bold uppercase tracking-[0.12em] text-steel-400 transition-colors duration-200 hover:border-forge hover:text-forge-light"
              >
                {t.quote.reset}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="space-y-5" noValidate>
              <div>
                <label htmlFor="q-name" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-forge-soft">
                  {t.quote.nameLabel}
                </label>
                <input
                  id="q-name"
                  data-testid="quote-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.quote.namePh}
                  className={field}
                />
              </div>
              <div>
                <label htmlFor="q-phone" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-forge-soft">
                  {t.quote.phoneLabel}
                </label>
                <input
                  id="q-phone"
                  type="tel"
                  data-testid="quote-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t.quote.phonePh}
                  className={field}
                />
              </div>
              <div>
                <label htmlFor="q-job" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-forge-soft">
                  {t.quote.jobLabel}
                </label>
                <textarea
                  id="q-job"
                  rows={5}
                  data-testid="quote-job-input"
                  value={form.job}
                  onChange={(e) => setForm({ ...form, job: e.target.value })}
                  placeholder={t.quote.jobPh}
                  className={`${field} resize-none`}
                />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-steel-400">
                {t.quote.photoNote} {PHONE_DISPLAY} {t.quote.photoNoteEnd}
              </p>
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit-btn"
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-forge px-6 py-4 font-head text-lg font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-forge-light disabled:opacity-60"
              >
                {sending ? <Loader2 size={19} className="animate-spin" /> : null}
                {sending ? t.quote.sending : t.quote.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
